import React from 'react'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Skeleton } from '@/shared/components/atoms/Skeleton'
import { PageLayout } from '@/shared/components/organisms/Layout/PageLayout/PageLayout'

import { AssignScheduleModal } from './components/AssignScheduleModal'
import Calendar from './components/Calendar/Calendar'
import { EditScheduleModal } from './components/EditScheduleModal'
import { CalendarEvent } from './context/Data'
import { useEvents } from './context/EventContext'
import { ScheduleDetailsHeader } from './ScheduleDetailsHeader'

interface ScheduleDetailsPageUIProps {
	id?: string
	isCreationMode: boolean
	isLoading: boolean
	error: any
	scheduleName: string
	scheduleDescription: string
	scheduleDetails: any
	initialCalendarEvents: CalendarEvent[]
	isReadOnly: boolean
	isEditModalOpen: boolean
	onRequestEdit: () => void
	setIsEditModalOpen: (open: boolean) => void
	isAssignModalOpen: boolean
	setIsAssignModalOpen: (open: boolean) => void
	refetchSchedule: () => void
	handleEditSave: (
		newTitle: string,
		newDescription: string,
		titleTranslations: Record<string, string>,
		descriptionTranslations: Record<string, string>,
		isPublic?: boolean,
		contentCategoryIds?: number[],
		currentEvents?: CalendarEvent[],
		resellerId?: number | null
	) => Promise<void>
	isFavorite: boolean
	onPreviewPlay: () => void
	onPreviewSkip?: () => void
	isPreviewPlaying: boolean
	isPreviewLoading: boolean
	isPreviewStarting?: boolean
	isPreviewDisabled: boolean
	isPreviewSkipDisabled?: boolean
}

const ScheduleDetailsPageUI: React.FC<ScheduleDetailsPageUIProps> = ({
	id,
	isCreationMode,
	isLoading,
	error,
	scheduleName,
	scheduleDescription,
	scheduleDetails: schedule,
	initialCalendarEvents,
	isReadOnly,
	isEditModalOpen,
	onRequestEdit,
	setIsEditModalOpen,
	isAssignModalOpen,
	setIsAssignModalOpen,
	refetchSchedule,
	handleEditSave,
	isFavorite,
	onPreviewPlay,
	onPreviewSkip,
	isPreviewPlaying,
	isPreviewLoading,
	isPreviewStarting = false,
	isPreviewDisabled,
	isPreviewSkipDisabled = false,
}) => {
	const { t, getTranslation, currentLanguage } = useTranslation('common')

	const title = getTranslation(
		schedule?.titleTranlations || {},
		currentLanguage,
		schedule?.title || ''
	)
	const description = getTranslation(
		schedule?.descriptionTranlations || {},
		currentLanguage,
		schedule?.description || ''
	)

	if (!isCreationMode && isLoading) {
		return (
			<PageLayout
				customHeader={
					<div className="space-y-2 flex-1">
						<Skeleton className="h-8 w-48 lg:h-10 lg:w-64" />
						<Skeleton className="h-4 w-full max-w-md lg:max-w-2xl" />
					</div>
				}
			>
				<Skeleton className="h-64 lg:h-96 w-full" />
			</PageLayout>
		)
	}

	if (!isCreationMode && error) {
		return (
			<PageLayout>
				<div className="text-center text-destructive">
					{t('entities.schedule.error_loading')}
				</div>
			</PageLayout>
		)
	}

	return (
		<>
			<PageLayout
				customHeader={
					<ScheduleDetailsHeader
						scheduleName={scheduleName}
						isCreationMode={isCreationMode}
						onRequestEdit={onRequestEdit}
						initialEvents={initialCalendarEvents}
						scheduleId={id}
						scheduleDescription={scheduleDescription}
						scheduleTitleTranslations={schedule?.titleTranlations}
						scheduleDescriptionTranslations={schedule?.descriptionTranlations}
						refetchSchedule={refetchSchedule}
						onOpenAssignModal={() => setIsAssignModalOpen(true)}
						isPublic={schedule?.public}
						contentCategoryIds={schedule?.contentCategoryIds || []}
						isReadOnly={isReadOnly}
						isFavorite={isFavorite}
						onPreviewPlay={onPreviewPlay}
						onPreviewSkip={onPreviewSkip}
						isPreviewPlaying={isPreviewPlaying}
						isPreviewLoading={isPreviewLoading}
						isPreviewStarting={isPreviewStarting}
						isPreviewDisabled={isPreviewDisabled}
						isPreviewSkipDisabled={isPreviewSkipDisabled}
					/>
				}
			>
				<Calendar readOnly={isReadOnly} />
			</PageLayout>

			<EventsContextConsumer
				onSave={handleEditSave}
				open={isEditModalOpen}
				onOpenChange={setIsEditModalOpen}
				initialTitle={title}
				initialDescription={description}
				initialTitleTranslations={schedule?.titleTranlations || {}}
				initialDescriptionTranslations={schedule?.descriptionTranlations || {}}
				initialPublic={schedule?.public || false}
				initialContentCategoryIds={schedule?.contentCategoryIds || []}
				initialResellerId={schedule?.resellerId ?? null}
			/>

			<AssignScheduleModal
				open={isAssignModalOpen}
				onOpenChange={setIsAssignModalOpen}
				scheduleId={id}
				scheduleName={scheduleName}
			/>
		</>
	)
}

// Inner component to access EventsContext
const EventsContextConsumer: React.FC<{
	onSave: any
	open: boolean
	onOpenChange: (open: boolean) => void
	initialTitle: string
	initialDescription: string
	initialTitleTranslations?: Record<string, string>
	initialDescriptionTranslations?: Record<string, string>
	initialPublic: boolean
	initialContentCategoryIds: number[]
	initialResellerId?: number | null
}> = ({ onSave, ...props }) => {
	const { events } = useEvents()

	return (
		<EditScheduleModal
			{...props}
			onSave={(
				title,
				desc,
				titleTrans,
				descTrans,
				isPublic,
				categs,
				resellerId
			) =>
				onSave(
					title,
					desc,
					titleTrans,
					descTrans,
					isPublic,
					categs,
					events,
					resellerId
				)
			}
		/>
	)
}

export default ScheduleDetailsPageUI
